import Phaser from 'phaser';
import { useGameStore } from '../store/gameStore';
import { getWinner, getPlayersByScore, isGameOver } from '@game/models/Game';
import { getPlayerColor } from '@game/utils/PlayerColors';
import { COLORS, TYPOGRAPHY, createCard } from '../ui/theme';

/**
 * GameOverScene: Display final scores and winner
 */
export class GameOverScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameOverScene' });
  }

  create(): void {
    const store = useGameStore.getState();
    const game = store.game;

    if (!game) {
      console.error('No game state found');
      return;
    }

    // Verify game is actually over
    if (!isGameOver(game)) {
      console.warn('Game is not over yet');
      this.scene.start('BoardScene');
      return;
    }

    const winner = getWinner(game);
    const playersByScore = getPlayersByScore(game);

    // Background with gradient
    this.add.rectangle(640, 360, 1280, 720, COLORS.background.primary);
    this.add.rectangle(640, 200, 1280, 400, COLORS.background.secondary, 0.6);

    // Main results card
    const cardWidth = 600;
    const cardHeight = 500;
    const cardX = (1280 - cardWidth) / 2;
    const cardY = (720 - cardHeight) / 2 - 20;
    
    createCard(this, cardX, cardY, cardWidth, cardHeight, 10);

    // Trophy or tie icon
    const icon = winner ? '🏆' : '🤝';
    this.add.text(640, cardY + 60, icon, {
      fontSize: '64px',
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(11);

    // Title
    const titleText = winner 
      ? `${winner.name} Wins!`
      : 'It\'s a Tie!';
    
    this.add.text(640, cardY + 120, titleText, {
      fontSize: TYPOGRAPHY.fontSize['4xl'],
      color: COLORS.text.accent,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(11);

    // Decorative line
    const lineGraphics = this.add.graphics();
    lineGraphics.fillStyle(COLORS.primary.main, 0.5);
    lineGraphics.fillRoundedRect(340, cardY + 150, 600, 2, 1);
    lineGraphics.setDepth(11);

    // Final Scores header
    this.add.text(640, cardY + 180, 'Final Scores', {
      fontSize: TYPOGRAPHY.fontSize['2xl'],
      color: COLORS.text.primary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(11);

    // Display each player's score in card format
    let yPos = cardY + 230;
    const spacing = 65;

    playersByScore.forEach((player, index) => {
      const isWinner = winner && player.id === winner.id;
      const playerColor = getPlayerColor(player.id);
      
      // Rank indicator
      const rank = index + 1;
      const rankText = rank === 1 ? '1st' : rank === 2 ? '2nd' : rank === 3 ? '3rd' : `${rank}th`;
      
      // Create mini card for each player
      const miniCardWidth = 500;
      const miniCardHeight = 50;
      const miniCardX = 640 - miniCardWidth / 2;
      const miniCardY = yPos - miniCardHeight / 2;
      
      const miniCard = this.add.graphics();
      if (isWinner) {
        // Winner gets special highlight
        miniCard.fillStyle(COLORS.primary.main, 0.2);
        miniCard.lineStyle(2, COLORS.primary.main, 1);
      } else {
        miniCard.fillStyle(COLORS.background.tertiary, 0.5);
        miniCard.lineStyle(1, COLORS.border.light, 0.5);
      }
      miniCard.fillRoundedRect(miniCardX, miniCardY, miniCardWidth, miniCardHeight, 8);
      miniCard.strokeRoundedRect(miniCardX, miniCardY, miniCardWidth, miniCardHeight, 8);
      miniCard.setDepth(11);

      // Color indicator circle
      const colorCircle = this.add.graphics();
      colorCircle.fillStyle(playerColor.primary, 1);
      colorCircle.fillCircle(miniCardX + 30, yPos, 10);
      colorCircle.setDepth(12);

      // Rank badge
      const rankBadge = this.add.graphics();
      rankBadge.fillStyle(COLORS.background.tertiary, 1);
      rankBadge.fillRoundedRect(miniCardX + 60, yPos - 15, 45, 30, 6);
      rankBadge.setDepth(12);
      
      this.add.text(miniCardX + 82, yPos, rankText, {
        fontSize: TYPOGRAPHY.fontSize.sm,
        color: COLORS.text.secondary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      }).setOrigin(0.5).setDepth(13);

      // Player name
      this.add.text(miniCardX + 130, yPos, player.name, {
        fontSize: isWinner ? TYPOGRAPHY.fontSize.xl : TYPOGRAPHY.fontSize.lg,
        color: isWinner ? COLORS.text.accent : COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      }).setOrigin(0, 0.5).setDepth(12);

      // Score
      this.add.text(miniCardX + miniCardWidth - 30, yPos, `${player.score}`, {
        fontSize: isWinner ? TYPOGRAPHY.fontSize['2xl'] : TYPOGRAPHY.fontSize.xl,
        color: isWinner ? COLORS.text.accent : COLORS.text.primary,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      }).setOrigin(1, 0.5).setDepth(12);

      this.add.text(miniCardX + miniCardWidth - 30, yPos + 16, 'points', {
        fontSize: TYPOGRAPHY.fontSize.xs,
        color: COLORS.text.disabled,
        fontFamily: TYPOGRAPHY.fontFamily.default,
      }).setOrigin(1, 0).setDepth(12);

      yPos += spacing;
    });

    // Game stats
    yPos += 20;
    const statsContainer = this.add.container(640, yPos);
    
    const statStyle = {
      fontSize: TYPOGRAPHY.fontSize.sm,
      color: COLORS.text.secondary,
      fontFamily: TYPOGRAPHY.fontFamily.default,
    };

    const totalWords = game.players.reduce((sum, p) => sum + p.wordsPlayed.length, 0);
    
    statsContainer.add([
      this.add.text(-150, 0, `Turns: ${game.turnNumber}`, statStyle).setOrigin(0.5),
      this.add.text(0, 0, `Words: ${totalWords}`, statStyle).setOrigin(0.5),
      this.add.text(150, 0, `Letters Left: ${game.letterPool.totalRemaining}`, statStyle).setOrigin(0.5),
    ]);
    
    statsContainer.setDepth(11);

    // Buttons
    this.createButtons();
  }

  private createButtons(): void {
    const buttonY = 650;
    const buttonWidth = 200;
    const buttonHeight = 50;

    // New Game button (primary)
    this.createModernButton(440, buttonY, buttonWidth, buttonHeight, 'New Game', COLORS.success.main, COLORS.success.light, COLORS.success.dark, () => {
      const store = useGameStore.getState();
      const currentGame = store.game;
      
      // Start new game with same players
      if (currentGame) {
        const playerNames = currentGame.players.map(p => p.name);
        store.startNewGame(playerNames);
      } else {
        store.startNewGame();
      }
      
      this.scene.start('PoolScene');
    });

    // View Board button (secondary)
    this.createModernButton(840, buttonY, buttonWidth, buttonHeight, 'View Board', COLORS.primary.main, COLORS.primary.light, COLORS.primary.dark, () => {
      this.scene.start('BoardScene');
    });
  }

  private createModernButton(
    x: number,
    y: number,
    width: number,
    height: number,
    text: string,
    colorMain: number,
    colorHover: number,
    colorActive: number,
    onClick: () => void
  ): void {
    // Shadow
    const shadow = this.add.graphics();
    shadow.fillStyle(0x000000, 0.3);
    shadow.fillRoundedRect(x - width / 2 + 3, y - height / 2 + 3, width, height, 8);
    shadow.setDepth(10);

    // Button background
    const bg = this.add.graphics();
    bg.fillStyle(colorMain, 1);
    bg.fillRoundedRect(x - width / 2, y - height / 2, width, height, 8);
    bg.setDepth(11);
    bg.setInteractive(
      new Phaser.Geom.Rectangle(x - width / 2, y - height / 2, width, height),
      Phaser.Geom.Rectangle.Contains
    );

    // Button text
    this.add.text(x, y, text, {
      fontSize: TYPOGRAPHY.fontSize.lg,
      color: '#ffffff',
      fontFamily: TYPOGRAPHY.fontFamily.default,
    }).setOrigin(0.5).setDepth(12);

    // Interactions
    bg.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(colorHover, 1);
      bg.fillRoundedRect(x - width / 2, y - height / 2, width, height, 8);
      this.input.setDefaultCursor('pointer');
    });

    bg.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(colorMain, 1);
      bg.fillRoundedRect(x - width / 2, y - height / 2, width, height, 8);
      this.input.setDefaultCursor('default');
    });

    bg.on('pointerdown', () => {
      bg.clear();
      bg.fillStyle(colorActive, 1);
      bg.fillRoundedRect(x - width / 2, y - height / 2, width, height, 8);
      
      this.time.delayedCall(100, onClick);
    });
  }
}
